// İş Ortağı Portalı kabuğu — tasarım teslimi vertex-partner-handoff 1.0 (15.10.2026).
// İç VERTEX kabuğunun (Sidebar + Header) YERİNE geçer: dış kullanıcıda sürüm etiketi,
// saniyeli saat, yapay zekâ düğmesi, görev ikonu ve yüzen sohbet düğmesi YOK (handoff §11).
// Menü görünürlüğü iç Sidebar ile AYNI kural: modül yetkisi + partnerPathAllowed +
// şirket yöneticisi bayrağı; guard'ın geçirmediği sayfa listelenmez.
import { useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Home, Package, ShoppingCart, ClipboardList, Truck, FileText, LayoutGrid,
  MessageSquare, Users, Bell, PanelLeftClose, TrendingUp, Boxes, Wallet, Settings, UserCircle, BellRing, KeyRound,
} from 'lucide-react'
import '../../../styles/partner-portal.css'
import api from '../../../services/api'
import { useAuth } from '../../../hooks/useAuth'
import { loginPath, partnerPathAllowed, prepareLogout } from '../../../utils/loginSurface'
import { useAuthStore } from '../../../stores/authStore'
import { authService } from '../../../services/authService'
import { ICON, usePartnerMe, shortCompany } from './ui'
import Header from '../../layout/Header'
import PartnerPasswordModal from './PartnerPasswordModal'

// Menü iç VERTEX menüsüyle AYNI düzen (kullanıcı isteği 16.10.2026): solda her zaman görünen
// dar simge şeridi + yanında başlığındaki düğmeyle kapanan sayfa paneli. Panel açık/kapalı
// tercihi tarayıcıda tutulur (iç menüdeki `vertex.sidebar.panelOpen` deseni).
const PANEL_KEY = 'vertex.partner.panelOpen'
const readPanelOpen = () => { try { return localStorage.getItem(PANEL_KEY) !== '0' } catch { return true } }

// Şeritte iç VERTEX'teki gibi MODÜL GRUPLARI (kullanıcı isteği 16.10.2026: "vertexteki gibi finans,
// satış, stok panelleri gruplansın"). Simgeye basınca panel o grubun sayfalarını açar; açık grubun
// simgesine yeniden basmak paneli kapatır. Bulunulan sayfanın grubu kendiliğinden seçilir.
// Panelden sayfa seçilince panel kapanır (kullanıcı isteği 16.10.2026: "seçim yaptıktan sonra daralsın").
// Ana sayfa menüde DEĞİL: şeritteki V logosu ana sayfaya götürür (kullanıcı isteği 16.10.2026).
const GROUP_KEY = 'vertex.partner.activeGroup'
const GROUPS = [
  { id: 'sales', label: 'Satış', icon: TrendingUp,
    paths: ['/partner/catalog', '/partner/cart', '/partner/orders', '/partner/shipments'] },
  { id: 'stock', label: 'Stok', icon: Boxes, paths: ['/partner/stock'] },
  { id: 'finance', label: 'Finans', icon: Wallet, paths: ['/partner/invoices'] },
  // Ayarlar (kullanıcı isteği 16.10.2026): şirket kullanıcıları + kişisel hesap ayarları; "Şifreyi değiştir"
  // sayfa değil, kabuğun açılır penceresidir (action).
  { id: 'settings', label: 'Ayarlar', icon: Settings,
    paths: ['/partner/users', '/settings/profile', '/settings/notifications', 'action:password'] },
]
const readGroup = () => { try { return localStorage.getItem(GROUP_KEY) || 'sales' } catch { return 'sales' } }

const NAV = [
  { to: '/partner', label: 'Ana sayfa', icon: Home, exact: true },
  { to: '/partner/catalog', label: 'Ürün kataloğu', icon: Package, module: 'PARTNER.CATALOG' },
  { to: '/partner/cart', label: 'Sepetim', icon: ShoppingCart, module: 'PARTNER.CART', cart: true },
  { to: '/partner/orders', label: 'Siparişlerim', icon: ClipboardList, module: 'PARTNER.ORDERS' },
  { to: '/partner/shipments', label: 'Sevkiyat takibi', icon: Truck, module: 'PARTNER.SHIPMENTS' },
  { to: '/partner/invoices', label: 'Belgelerim', icon: FileText, module: 'PARTNER.INVOICES' },
  { to: '/partner/stock', label: 'Stok listem', icon: LayoutGrid, module: 'PARTNER.STOCK_OWN' },
  { to: '/partner/users', label: 'Kullanıcılar', icon: Users, partnerAdmin: true },
  { to: '/settings/profile', label: 'Profilim', icon: UserCircle },
  { to: '/settings/notifications', label: 'Bildirim ayarları', icon: BellRing },
  { to: 'action:password', label: 'Şifreyi değiştir', icon: KeyRound, action: 'password' },
]

/** Yazı tipleri yalnız portal açılınca yüklenir (iç uygulama Figtree kullanır). */
function usePortalFonts() {
  useEffect(() => {
    const id = 'pp-fonts'
    if (document.getElementById(id)) return
    const l = document.createElement('link')
    l.id = id
    l.rel = 'stylesheet'
    l.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;600&display=swap'
    document.head.appendChild(l)
  }, [])
}

export function PortalRoot({ children, className = '' }) {
  usePortalFonts()
  return <div className={`pp ${className}`}>{children}</div>
}

export function useUnreadNotifications() {
  return useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: async () => {
      const r = await api.get('/notifications/', { params: { limit: 10, unread_only: true }, timeout: 10000, silentNetToast: true })
      return r.data?.data || { items: [], total: 0 }
    },
    refetchInterval: 60_000,
  })
}

export function BellButton({ large = false, rail = false }) {
  const navigate = useNavigate()
  const { data } = useUnreadNotifications()
  const unread = data?.unread_count ?? data?.total ?? 0
  return (
    <button type="button" onClick={() => navigate('/notifications')}
            className={rail ? 'rail__btn' : `icon-btn ${large ? 'icon-btn--lg icon-btn--outline' : ''}`}
            title={rail ? 'Bildirimler' : undefined}
            aria-label={unread ? `Bildirimler, ${unread} okunmamış` : 'Bildirimler'}
            style={large || rail ? undefined : { position: 'relative' }}>
      <Bell {...ICON} />
      {unread > 0 && <span className="dot" aria-hidden="true">{unread > 99 ? '99+' : unread}</span>}
    </button>
  )
}

export default function PartnerShell({ children }) {
  const { user, hasPermission } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { data } = usePartnerMe()
  const me = data?.data || {}
  const cartCount = Number(me.cart?.item_count || 0)
  const isAdmin = !!user?.partner_is_admin
  const logout = useAuthStore((st) => st.logout)
  // Zorunlu şifre değişikliği penceresindeki "Çıkış yap" — kullanıcı açıkça bastığı için
  // ayrıca onay sorulmaz; kapı hesap türünden (iş ortağı /partner/login).
  const onLogout = () => { prepareLogout(user); logout(); navigate(loginPath(), { replace: true }) }

  // İzin senkronu: yönetim ekranında şirket izni değişince iş ortağı yeniden giriş yapmadan görsün —
  // 60 sn'de bir ve sekmeye dönüşte `/auth/me` ile matris tazelenir (Layout açılıştaki tazelemenin devamı).
  useEffect(() => {
    let alive = true
    const refresh = () => {
      authService.getMe().then((res) => {
        const fresh = res?.permissions || res?.user?.permissions
        if (!alive || !fresh) return
        const cur = useAuthStore.getState().user
        if (cur && JSON.stringify(cur.permissions || {}) !== JSON.stringify(fresh)) {
          useAuthStore.getState().setUser({ ...cur, permissions: fresh })
        }
      }).catch(() => { /* sessiz */ })
    }
    const t = setInterval(refresh, 60_000)
    const onVis = () => { if (document.visibilityState === 'visible') refresh() }
    document.addEventListener('visibilitychange', onVis)
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', onVis) }
  }, [])

  const items = NAV.filter((it) => {
    if (!it.action && !partnerPathAllowed(it.to)) return false
    if (it.partnerAdmin && !isAdmin) return false
    return !it.module || hasPermission(it.module, 'View', 'Own')
  })
  const isActive = (to, exact) => (exact
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(`${to}/`))
  const onChat = isActive('/partner/chat')
  const chatAllowed = partnerPathAllowed('/partner/chat') && hasPermission('PARTNER.CHAT', 'View', 'Own')

  // Gruplar yalnız görülebilen sayfalarıyla; boş grup şeritte çizilmez
  const groups = GROUPS
    .map((g) => ({ ...g, items: g.paths.map((p) => items.find((it) => it.to === p)).filter(Boolean) }))
    .filter((g) => g.items.length > 0)
  const groupOfPath = (path) => {
    let best = null; let len = -1
    for (const g of groups) {
      for (const it of g.items) {
        if (it.action) continue
        const hit = it.exact ? path === it.to : (path === it.to || path.startsWith(`${it.to}/`))
        if (hit && it.to.length > len) { best = g.id; len = it.to.length }
      }
    }
    return best
  }

  const [panelOpen, setPanelOpen] = useState(readPanelOpen)
  const setPanel = (v) => {
    setPanelOpen(v)
    try { localStorage.setItem(PANEL_KEY, v ? '1' : '0') } catch { /* depolama kapalı */ }
  }
  const [groupId, setGroupIdState] = useState(readGroup)
  const setGroupId = (id) => {
    setGroupIdState(id)
    try { localStorage.setItem(GROUP_KEY, id) } catch { /* depolama kapalı */ }
  }
  const routeGroup = groupOfPath(location.pathname)
  useEffect(() => {
    if (routeGroup && routeGroup !== groupId) setGroupId(routeGroup)
  }, [routeGroup]) // eslint-disable-line react-hooks/exhaustive-deps
  const activeGroup = groups.find((g) => g.id === groupId) || groups[0]
  const onGroup = (g) => {
    if (onChat) { setGroupId(g.id); setPanel(true); navigate(g.items[0].to); return }
    if (g.id === activeGroup?.id && panelOpen) { setPanel(false); return }
    setGroupId(g.id); setPanel(true)
  }

  // Üst çubuktaki "Sayfa ara" yalnız partnerin görebildiği sayfaları bulur
  const searchPages = [
    ...items.filter((it) => !it.action).map((it) => ({ label: it.label, path: it.to })),
    ...(chatAllowed ? [{ label: 'Sohbet', path: '/partner/chat' }] : []),
    { label: 'Bildirimler', path: '/notifications' },
  ].filter((p) => partnerPathAllowed(p.path))

  // Şifre penceresi: ilk girişte zorunlu, menüden isteğe bağlı
  const [pwOpen, setPwOpen] = useState(false)
  const mustChange = !!user?.must_change_password

  const name = user?.display_name || user?.username || 'Kullanıcı'
  const company = me.company?.name || ''

  return (
    <PortalRoot>
      <div className="app">
        {/* Dar şerit — her zaman görünür */}
        <nav className="rail" aria-label="Modüller">
          <NavLink to="/partner" end className="rail__logo" aria-label="Ana sayfa" title="Ana sayfa">V</NavLink>
          <span className="rail__sep" aria-hidden="true" />
          {groups.map((g) => {
            const Icon = g.icon
            const open = panelOpen && !onChat && activeGroup?.id === g.id
            const current = !onChat && routeGroup === g.id
            const hasCart = g.items.some((it) => it.cart)
            return (
              <button key={g.id} type="button" title={g.label} aria-label={g.label}
                      aria-expanded={open} aria-controls="pp-navpanel"
                      className={`rail__btn rail__btn--group ${open ? 'is-active' : ''} ${current ? 'is-current' : ''}`}
                      onClick={() => onGroup(g)}>
                <Icon {...ICON} size={18} />
                <span className="rail__label">{g.label}</span>
                {hasCart && cartCount > 0 && <span className="rail__badge" aria-label={`sepette ${cartCount} ürün`}>{cartCount}</span>}
              </button>
            )
          })}
          <span className="rail__sep" aria-hidden="true" />
          {chatAllowed && (
            <NavLink to="/partner/chat" className={`rail__btn ${onChat ? 'is-current' : ''}`} title="Sohbet" aria-label="Sohbet">
              <MessageSquare {...ICON} size={18} />
              <span className="rail__label">Sohbet</span>
            </NavLink>
          )}
          <span className="rail__spacer" />
        </nav>

        {/* Sayfa paneli — başlıktaki düğmeyle kapanır, şeritteki simgeyle açılır */}
        {panelOpen && !onChat && activeGroup && (
          <aside className="navpanel" id="pp-navpanel" aria-label="İş Ortağı sayfaları">
            <div className="navpanel__head">
              <h2>{activeGroup.label}</h2>
              <button type="button" className="icon-btn" aria-label="Paneli kapat" title="Paneli kapat" onClick={() => setPanel(false)}>
                <PanelLeftClose {...ICON} />
              </button>
            </div>
            <div className="navpanel__list">
              {activeGroup.items.map((it) => {
                const Icon = it.icon
                if (it.action === 'password') {
                  return (
                    <button key={it.to} type="button" className="nav-link"
                            onClick={() => { setPanel(false); setPwOpen(true) }}>
                      <Icon {...ICON} />
                      <span className="nav-link__label">{it.label}</span>
                    </button>
                  )
                }
                return (
                  <NavLink key={it.to} to={it.to} end={!!it.exact} className="nav-link"
                           onClick={() => setPanel(false)}
                           aria-current={isActive(it.to, it.exact) ? 'page' : undefined}>
                    <Icon {...ICON} />
                    <span className="nav-link__label">{it.label}</span>
                    {it.cart && cartCount > 0 && (
                      <span className="nav-link__count" aria-label={`sepette ${cartCount} ürün`}>{cartCount}</span>
                    )}
                  </NavLink>
                )
              })}
            </div>
            <div className="navpanel__foot">
              <span className="navpanel__mark" aria-hidden="true">{(shortCompany(company) || 'V').charAt(0)}</span>
              <span className="navpanel__who">
                <strong title={company}>{shortCompany(company) || 'İş Ortağı'}</strong>
                <small title={name}>{name}</small>
              </span>
            </div>
          </aside>
        )}
        <div className="main-col">
          {/* Üst çubuk iç VERTEX'in aynısı (variant="partner": AI, onay rozeti, firma seçici yok) */}
          <Header variant="partner" searchPages={searchPages} onMenu={() => setPanel(!panelOpen)}
                  onChangePassword={() => setPwOpen(true)} />
          <main className="content">{children}</main>
        </div>
        {(mustChange || pwOpen) && (
          <PartnerPasswordModal forced={mustChange} onLogout={onLogout}
                                onClose={() => {
                                  setPwOpen(false)
                                  // Eski bağlantıyla güvenlik sayfasına gelindiyse ana sayfaya dön
                                  if (location.pathname.startsWith('/settings/security')) navigate('/partner')
                                }} />
        )}
      </div>
    </PortalRoot>
  )
}
